import type { Biome, Pace, Weather } from "@/game/types";
import { cn } from "@/lib/utils";
import type { TimeOfDay } from "@/game/types";

interface TravelSceneProps {
  /** Whether the scene should actively scroll (wagon rolling) */
  moving: boolean;
  /** Travel pace — scales scroll speed */
  pace: Pace;
  /** Current weather overlay */
  weather: Weather;
  /** Biome for backdrop + passing scenery */
  biome: Biome;
  /** Gradual day/night cycle position */
  timeOfDay: TimeOfDay;
  className?: string;
}

// Pace scales the base scroll speed. Higher multiplier = faster scroll
// (shorter animation duration).
const PACE_SPEED: Record<Pace, number> = {
  steady: 1,
  strenuous: 1.45,
  grueling: 2,
};

/**
 * A side-scrolling, Oregon Trail-style travel scene with parallax layers,
 * an ox-drawn wagon, weather effects, biome backdrops, and passing scenery.
 */
export function TravelScene({
  moving,
  pace,
  weather,
  biome,
  timeOfDay,
  className,
}: TravelSceneProps) {
  const speed = PACE_SPEED[pace];
  const isNight = timeOfDay === "night";
  const isDusk = timeOfDay === "dusk";
  const isDawn = timeOfDay === "dawn";
  const isStorm = weather === "storm";
  // The party makes camp when night falls and the wagon is parked (idle).
  const camping = isNight && !moving;

  // Base durations divided by the pace multiplier when moving; very slow idle.
  const dur = (base: number) => (moving ? base / speed : base * 6);

  return (
    <div
      className={cn(
        "relative h-40 w-full overflow-hidden rounded-lg border-2 border-primary/30 transition-[background] duration-1000 sm:h-52",
        skyClass(weather, timeOfDay),
        className,
      )}
      aria-hidden
    >
      {/* Sun / moon — arcs across depending on time of day */}
      <CelestialBody timeOfDay={timeOfDay} storm={isStorm} />

      {/* Stars appear at night and faintly at dusk/dawn */}
      {(isNight || isDusk || isDawn) && <Stars dim={!isNight} />}

      {/* Birds fly across during daylight hours (not at night/storm) */}
      {!isNight && !isStorm && <Birds />}

      {/* Clouds — slow parallax (heavier/darker in storms) */}
      <ScrollBand dur={dur(26)} className="top-3 h-16 opacity-90">
        <CloudRow storm={isStorm} night={isNight} dusk={isDusk || isDawn} />
      </ScrollBand>

      {/* Biome backdrop — sits behind the hills, medium-slow parallax */}
      <ScrollBand dur={dur(22)} className="bottom-9 h-28">
        <BackdropRow biome={biome} />
      </ScrollBand>

      {/* Near hills — medium */}
      <ScrollBand dur={dur(11)} className="bottom-6 h-20">
        <HillRow tone="near" />
      </ScrollBand>

      {/* Passing scenery — same speed as ground, foreground */}
      <ScrollBand dur={dur(6)} className="bottom-2 h-14 z-10">
        <SceneryRow biome={biome} />
      </ScrollBand>

      {/* A herd of bison occasionally crosses the plains/canyon */}
      {moving && (biome === "plains" || biome === "canyon") && <BisonHerd dur={dur(16)} />}

      {/* Ground — fast */}
      <ScrollBand dur={dur(5)} className="bottom-0 h-10">
        <GroundRow biome={biome} />
      </ScrollBand>

      {/* Camp OR the rolling wagon */}
      {camping ? (
        <Camp />
      ) : (
        <>
          <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
            <Wagon moving={moving} />
          </div>
          <div className="absolute bottom-4 left-1/2 z-10 h-2 w-40 -translate-x-1/2 rounded-full bg-black/25 blur-sm" />
        </>
      )}

      {/* Weather overlays (on top of everything) */}
      {(weather === "rain" || isStorm) && <Rain heavy={isStorm} />}
      {weather === "snow" && <Snow />}
      {isStorm && (
        <div className="lightning-flash pointer-events-none absolute inset-0 z-30 bg-white" />
      )}

      {/* Nighttime / dusk darkening tint */}
      {isNight && (
        <div className="pointer-events-none absolute inset-0 z-[25] bg-indigo-950/45" />
      )}
      {isDusk && (
        <div className="pointer-events-none absolute inset-0 z-[25] bg-orange-900/15" />
      )}

      {/* Time + weather label */}
      <div className="absolute left-2 top-2 z-40 rounded bg-black/40 px-2 py-1 font-terminal text-sm leading-none text-white backdrop-blur-sm">
        {timeLabel(timeOfDay)} · {weatherLabel(weather)}
      </div>
    </div>
  );
}

function timeLabel(t: TimeOfDay): string {
  switch (t) {
    case "dawn":
      return "🌅 Dawn";
    case "dusk":
      return "🌇 Dusk";
    case "night":
      return "🌙 Night";
    default:
      return "🌤️ Day";
  }
}

function weatherLabel(weather: Weather): string {
  switch (weather) {
    case "storm":
      return "⛈️ Storm";
    case "rain":
      return "🌧️ Rain";
    case "snow":
      return "❄️ Snow";
    default:
      return "Clear";
  }
}

function skyClass(weather: Weather, time: TimeOfDay): string {
  if (weather === "storm") {
    return "bg-gradient-to-b from-slate-600 to-slate-400 dark:from-slate-800 dark:to-slate-700";
  }
  // Time of day drives the base sky; weather lightly modifies mood via clouds/overlays.
  switch (time) {
    case "night":
      return "bg-gradient-to-b from-indigo-950 via-indigo-900 to-slate-800";
    case "dusk":
      return "bg-gradient-to-b from-indigo-700 via-orange-400 to-amber-300";
    case "dawn":
      return "bg-gradient-to-b from-indigo-400 via-rose-300 to-amber-200";
    case "day":
    default:
      if (weather === "rain")
        return "bg-gradient-to-b from-slate-400 to-blue-200 dark:from-slate-700 dark:to-slate-600";
      if (weather === "snow")
        return "bg-gradient-to-b from-slate-300 to-blue-100 dark:from-slate-600 dark:to-slate-500";
      return "bg-gradient-to-b from-sky-300 to-amber-100 dark:from-indigo-900 dark:to-purple-900";
  }
}

/** Sun or moon positioned by time of day, arcing across the sky. */
function CelestialBody({ timeOfDay, storm }: { timeOfDay: TimeOfDay; storm: boolean }) {
  if (timeOfDay === "night") {
    return (
      <div className="absolute right-10 top-4 z-0">
        <div className="relative size-9 rounded-full bg-slate-100 shadow-[0_0_24px_6px_rgba(226,232,240,0.5)]">
          {/* craters */}
          <span className="absolute left-2 top-2 size-1.5 rounded-full bg-slate-300/70" />
          <span className="absolute right-2 top-3 size-1 rounded-full bg-slate-300/70" />
          <span className="absolute bottom-2 left-3 size-1 rounded-full bg-slate-300/70" />
        </div>
      </div>
    );
  }
  // Sun position: dawn low-left, day high-center-right, dusk low-right.
  const pos =
    timeOfDay === "dawn"
      ? "left-10 top-16"
      : timeOfDay === "dusk"
        ? "right-10 top-16"
        : "right-16 top-4";
  const color =
    timeOfDay === "day"
      ? "bg-yellow-300 shadow-[0_0_34px_10px_rgba(253,224,71,0.6)]"
      : "bg-orange-400 shadow-[0_0_34px_12px_rgba(251,146,60,0.55)]";
  return (
    <div
      className={cn(
        "absolute z-0 size-10 rounded-full transition-all duration-1000",
        pos,
        storm ? "bg-yellow-200/40" : color,
      )}
    />
  );
}

/** A full-width band whose children repeat twice and scroll seamlessly. */
function ScrollBand({
  dur,
  className,
  children,
}: {
  dur: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("absolute inset-x-0", className)}>
      <div
        className="scene-layer flex h-full w-[200%]"
        style={{ "--scroll-dur": `${dur}s` } as React.CSSProperties}
      >
        <div className="flex h-full w-1/2 shrink-0">{children}</div>
        <div className="flex h-full w-1/2 shrink-0">{children}</div>
      </div>
    </div>
  );
}

function Stars({ dim }: { dim?: boolean }) {
  const stars = Array.from({ length: 14 }, (_, i) => ({
    left: (i * 37) % 100,
    top: (i * 53) % 55,
    dur: 2 + (i % 4),
  }));
  return (
    <div className={cn("pointer-events-none absolute inset-0", dim && "opacity-40")}>
      {stars.map((s, i) => (
        <span
          key={i}
          className="twinkle absolute size-[3px] rounded-full bg-white"
          style={
            {
              left: `${s.left}%`,
              top: `${s.top}%`,
              "--twinkle-dur": `${s.dur}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

/** Birds gliding across the sky with flapping wings. */
function Birds() {
  const flock = [
    { top: 18, dur: 15, delay: 0, scale: 1 },
    { top: 26, dur: 18, delay: 4, scale: 0.8 },
    { top: 14, dur: 22, delay: 9, scale: 0.7 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {flock.map((b, i) => (
        <div
          key={i}
          className="bird-fly absolute"
          style={
            {
              top: `${b.top}%`,
              transform: `scale(${b.scale})`,
              animationDelay: `${b.delay}s`,
              "--bird-dur": `${b.dur}s`,
            } as React.CSSProperties
          }
        >
          <div className="flex gap-3">
            <Bird />
            <Bird />
          </div>
        </div>
      ))}
    </div>
  );
}

function Bird() {
  return (
    <svg width="18" height="10" viewBox="0 0 18 10" className="wing-flap">
      <path
        d="M1 6 Q 5 1 9 6 Q 13 1 17 6"
        className="fill-none stroke-slate-700/70 dark:stroke-slate-200/70"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A herd of bison thundering across the foreground. */
function BisonHerd({ dur }: { dur: number }) {
  return (
    <div
      className="herd-cross pointer-events-none absolute bottom-3 left-0 z-[15]"
      style={{ "--herd-dur": `${dur * 1.1}s` } as React.CSSProperties}
    >
      <div className="flex items-end gap-3">
        <Bison scale={0.9} />
        <Bison scale={1.1} />
        <Bison scale={0.8} />
      </div>
    </div>
  );
}

function Bison({ scale = 1 }: { scale?: number }) {
  return (
    <div className="gallop-bob" style={{ transform: `scale(${scale})` }}>
      <svg width="46" height="30" viewBox="0 0 46 30">
        {/* body */}
        <ellipse cx="24" cy="15" rx="16" ry="9" className="fill-amber-950 dark:fill-stone-800" />
        {/* hump */}
        <path d="M12 12 Q 16 2 24 8 L24 15 Z" className="fill-amber-900 dark:fill-stone-700" />
        {/* head */}
        <path d="M8 12 q-7 1 -6 8 q1 4 7 3 l3 -3 z" className="fill-stone-950 dark:fill-stone-900" />
        {/* horn */}
        <path d="M4 12 q-3 -3 0 -5" className="fill-none stroke-stone-300" strokeWidth="1.4" />
        {/* legs */}
        <g className="stroke-amber-950 dark:stroke-stone-800" strokeWidth="3">
          <line x1="16" y1="22" x2="16" y2="29" />
          <line x1="24" y1="22" x2="24" y2="29" />
          <line x1="32" y1="22" x2="32" y2="29" />
        </g>
      </svg>
    </div>
  );
}

/** Nighttime camp: parked wagon, campfire, resting party. */
function Camp() {
  const embers = Array.from({ length: 6 }, (_, i) => ({
    left: 48 + ((i * 7) % 10),
    delay: (i * 0.4) % 2.4,
    dur: 2 + (i % 3) * 0.5,
  }));
  return (
    <div className="absolute inset-x-0 bottom-4 z-20">
      {/* Parked wagon, slightly left */}
      <div className="absolute bottom-0 left-[26%] -translate-x-1/2 scale-90">
        <Wagon moving={false} />
      </div>

      {/* Campfire, center-right */}
      <div className="absolute bottom-1 left-[58%] -translate-x-1/2">
        {/* glow */}
        <div className="glow-pulse absolute -inset-6 rounded-full bg-orange-400/30 blur-md" />
        {/* embers */}
        {embers.map((e, i) => (
          <span
            key={i}
            className="ember-rise absolute bottom-4 size-1 rounded-full bg-orange-300"
            style={
              {
                left: `${e.left}%`,
                animationDelay: `${e.delay}s`,
                "--ember-dur": `${e.dur}s`,
              } as React.CSSProperties
            }
          />
        ))}
        <svg width="60" height="46" viewBox="0 0 60 46" className="relative">
          {/* logs */}
          <g className="stroke-amber-900" strokeWidth="4" strokeLinecap="round">
            <line x1="18" y1="40" x2="42" y2="36" />
            <line x1="18" y1="36" x2="42" y2="40" />
          </g>
          {/* flame */}
          <g className="flame-flicker">
            <path d="M30 38 Q 20 26 30 10 Q 40 26 30 38 Z" className="fill-orange-500" />
            <path d="M30 38 Q 24 28 30 18 Q 36 28 30 38 Z" className="fill-yellow-300" />
          </g>
        </svg>
      </div>

      {/* A resting companion silhouette by the fire */}
      <div className="absolute bottom-2 left-[72%] text-2xl">🧑‍🌾</div>

      {/* ground shadow */}
      <div className="absolute bottom-0 left-[26%] h-2 w-36 -translate-x-1/2 rounded-full bg-black/30 blur-sm" />
    </div>
  );
}

function CloudRow({
  storm,
  night,
  dusk,
}: {
  storm?: boolean;
  night?: boolean;
  dusk?: boolean;
}) {
  const fill = storm
    ? "fill-slate-500/80 dark:fill-slate-900/80"
    : night
      ? "fill-slate-400/40"
      : dusk
        ? "fill-orange-200/70"
        : "fill-white/90 dark:fill-slate-300/60";
  return (
    <svg viewBox="0 0 400 60" preserveAspectRatio="none" className="h-full w-full">
      <g className={fill}>
        <ellipse cx="60" cy="26" rx="34" ry="14" />
        <ellipse cx="90" cy="30" rx="26" ry="12" />
        <ellipse cx="250" cy="18" rx="40" ry="15" />
        <ellipse cx="290" cy="24" rx="28" ry="12" />
      </g>
    </svg>
  );
}

function HillRow({ tone }: { tone: "near" }) {
  const fill =
    tone === "near"
      ? "fill-green-700/70 dark:fill-emerald-900"
      : "fill-green-600/50";
  return (
    <svg viewBox="0 0 400 100" preserveAspectRatio="none" className="h-full w-full">
      <path
        className={fill}
        d="M0 100 L0 55 Q 50 20 110 50 Q 170 78 230 42 Q 300 8 360 50 L400 45 L400 100 Z"
      />
    </svg>
  );
}

/** Distinct distant backdrop per biome. */
function BackdropRow({ biome }: { biome: Biome }) {
  switch (biome) {
    case "mountains":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <path className="fill-slate-500/70 dark:fill-slate-700" d="M0 120 L60 30 L120 90 L190 20 L260 85 L330 25 L400 100 L400 120 Z" />
          {/* snow caps */}
          <path className="fill-slate-100" d="M60 30 L48 48 L72 48 Z M190 20 L176 42 L204 42 Z M330 25 L318 45 L342 45 Z" />
        </svg>
      );
    case "fort":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <g className="fill-amber-800/80 dark:fill-amber-900">
            <rect x="120" y="55" width="160" height="60" />
            {/* palisade tips */}
            {Array.from({ length: 16 }, (_, i) => (
              <polygon key={i} points={`${120 + i * 10},55 ${125 + i * 10},46 ${130 + i * 10},55`} />
            ))}
            {/* watchtowers */}
            <rect x="108" y="42" width="18" height="73" />
            <rect x="274" y="42" width="18" height="73" />
          </g>
          {/* BCH flag */}
          <g>
            <rect x="199" y="20" width="2" height="24" className="fill-stone-700" />
            <path d="M201 20 h20 l-5 6 l5 6 h-20 z" className="fill-green-500" />
          </g>
        </svg>
      );
    case "market":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          {[30, 130, 230, 330].map((x, i) => (
            <g key={i}>
              <rect x={x} y="70" width="70" height="45" className="fill-amber-700/70 dark:fill-amber-900" />
              {/* striped awning */}
              <path d={`M${x - 4} 70 h78 l-8 14 h-62 z`} className={i % 2 ? "fill-red-500/80" : "fill-emerald-500/80"} />
            </g>
          ))}
        </svg>
      );
    case "canyon":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <path className="fill-orange-800/70 dark:fill-orange-950" d="M0 120 L0 40 L70 40 L70 75 L150 75 L150 30 L240 30 L240 70 L330 70 L330 45 L400 45 L400 120 Z" />
        </svg>
      );
    case "city":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <g className="fill-slate-600/80 dark:fill-slate-800">
            {[20, 70, 115, 165, 215, 265, 315, 360].map((x, i) => {
              const h = 40 + ((i * 29) % 60);
              return <rect key={i} x={x} y={120 - h} width="34" height={h} />;
            })}
          </g>
          {/* lit windows */}
          <g className="fill-yellow-300/80">
            {Array.from({ length: 24 }, (_, i) => (
              <rect key={i} x={26 + (i % 8) * 46} y={70 + Math.floor(i / 8) * 14} width="4" height="6" />
            ))}
          </g>
        </svg>
      );
    case "river":
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <rect x="0" y="80" width="400" height="40" className="fill-blue-400/60 dark:fill-blue-800/70" />
          <g className="stroke-blue-200/70" strokeWidth="2" fill="none">
            <path d="M0 92 q20 -5 40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0" />
            <path d="M0 104 q20 -5 40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0" />
          </g>
        </svg>
      );
    case "plains":
    default:
      return (
        <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full">
          <path className="fill-green-600/40 dark:fill-emerald-800/70" d="M0 120 L0 70 Q 100 45 200 68 Q 300 88 400 62 L400 120 Z" />
        </svg>
      );
  }
}

function GroundRow({ biome }: { biome: Biome }) {
  const groundFill =
    biome === "canyon"
      ? "fill-orange-300 dark:fill-orange-950/80"
      : biome === "mountains"
        ? "fill-slate-200 dark:fill-slate-800"
        : "fill-amber-200 dark:fill-amber-950/80";
  return (
    <svg viewBox="0 0 400 40" preserveAspectRatio="none" className="h-full w-full">
      <rect width="400" height="40" className={groundFill} />
      <g className="stroke-amber-500/70 dark:stroke-amber-700" strokeWidth="3">
        <line x1="20" y1="20" x2="60" y2="20" />
        <line x1="110" y1="20" x2="150" y2="20" />
        <line x1="200" y1="20" x2="240" y2="20" />
        <line x1="290" y1="20" x2="330" y2="20" />
      </g>
      <g className="fill-green-800/60 dark:fill-emerald-900">
        <circle cx="80" cy="10" r="3" />
        <circle cx="180" cy="30" r="3" />
        <circle cx="320" cy="12" r="3" />
      </g>
    </svg>
  );
}

/** Foreground passing scenery — varies by biome. */
function SceneryRow({ biome }: { biome: Biome }) {
  return (
    <svg viewBox="0 0 400 56" preserveAspectRatio="xMidYMax meet" className="h-full w-full">
      {biome === "plains" && (
        <>
          <Cactus x={40} />
          <Cactus x={250} scale={0.8} />
          <PassingWagon x={330} />
        </>
      )}
      {biome === "river" && (
        <>
          <Reed x={30} />
          <Reed x={70} />
          <Reed x={300} />
          <Reed x={340} />
        </>
      )}
      {biome === "fort" && (
        <>
          <Barrel x={60} />
          <Tree x={260} />
          <PassingWagon x={340} />
        </>
      )}
      {biome === "mountains" && (
        <>
          <Pine x={50} />
          <Pine x={120} scale={0.8} />
          <Pine x={300} />
          <Rock x={220} />
        </>
      )}
      {biome === "market" && (
        <>
          <Barrel x={40} />
          <Barrel x={70} />
          <Crate x={280} />
          <PassingWagon x={350} />
        </>
      )}
      {biome === "canyon" && (
        <>
          <Cactus x={60} />
          <Rock x={200} />
          <Rock x={320} scale={1.2} />
        </>
      )}
      {biome === "city" && (
        <>
          <LampPost x={60} />
          <LampPost x={220} />
          <Tree x={330} />
        </>
      )}
    </svg>
  );
}

/* ---- Little scenery sprites (all anchored to y≈52 baseline) ---- */

function Cactus({ x, scale = 1 }: { x: number; scale?: number }) {
  return (
    <g transform={`translate(${x},${52}) scale(${scale})`} className="fill-green-700 dark:fill-emerald-600">
      <rect x="-4" y="-30" width="8" height="30" rx="4" />
      <rect x="-14" y="-22" width="6" height="14" rx="3" />
      <rect x="-14" y="-22" width="6" height="4" />
      <rect x="8" y="-26" width="6" height="16" rx="3" />
    </g>
  );
}

function Reed({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`} className="stroke-green-700 dark:stroke-emerald-500" strokeWidth="2">
      <line x1="0" y1="0" x2="-2" y2="-22" />
      <line x1="4" y1="0" x2="6" y2="-18" />
      <line x1="8" y1="0" x2="10" y2="-24" />
    </g>
  );
}

function Pine({ x, scale = 1 }: { x: number; scale?: number }) {
  return (
    <g transform={`translate(${x},${52}) scale(${scale})`}>
      <rect x="-2" y="-8" width="4" height="8" className="fill-amber-900" />
      <polygon points="0,-34 -10,-14 10,-14" className="fill-green-800 dark:fill-emerald-700" />
      <polygon points="0,-26 -8,-8 8,-8" className="fill-green-700 dark:fill-emerald-600" />
    </g>
  );
}

function Tree({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`}>
      <rect x="-3" y="-12" width="6" height="12" className="fill-amber-900" />
      <circle cx="0" cy="-22" r="12" className="fill-green-700 dark:fill-emerald-600" />
    </g>
  );
}

function Rock({ x, scale = 1 }: { x: number; scale?: number }) {
  return (
    <g transform={`translate(${x},${52}) scale(${scale})`} className="fill-stone-500 dark:fill-stone-700">
      <path d="M-12 0 Q -14 -12 -2 -14 Q 12 -16 14 -2 L14 0 Z" />
    </g>
  );
}

function Barrel({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`}>
      <rect x="-7" y="-16" width="14" height="16" rx="3" className="fill-amber-700 dark:fill-amber-800" />
      <line x1="-7" y1="-10" x2="7" y2="-10" className="stroke-amber-950" strokeWidth="1.5" />
    </g>
  );
}

function Crate({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`}>
      <rect x="-9" y="-16" width="18" height="16" className="fill-amber-600 dark:fill-amber-800" />
      <path d="M-9 -16 L9 0 M9 -16 L-9 0" className="stroke-amber-900" strokeWidth="1.5" fill="none" />
    </g>
  );
}

function LampPost({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`}>
      <rect x="-1.5" y="-34" width="3" height="34" className="fill-slate-600 dark:fill-slate-400" />
      <circle cx="0" cy="-36" r="4" className="fill-yellow-300 drop-shadow-[0_0_6px_rgba(253,224,71,0.8)]" />
    </g>
  );
}

/** A ghostly other wagon that passes by. */
function PassingWagon({ x }: { x: number }) {
  return (
    <g transform={`translate(${x},${52})`} opacity="0.85">
      <rect x="-16" y="-14" width="32" height="8" rx="2" className="fill-amber-950 dark:fill-amber-900" />
      <path d="M-14 -14 Q -12 -30 0 -30 Q 12 -30 14 -14 Z" className="fill-stone-100 dark:fill-stone-300" />
      <circle cx="-9" cy="-4" r="4" className="fill-none stroke-stone-800 dark:stroke-stone-200" strokeWidth="1.5" />
      <circle cx="9" cy="-4" r="4" className="fill-none stroke-stone-800 dark:stroke-stone-200" strokeWidth="1.5" />
    </g>
  );
}

/* ---- Weather overlays ---- */

function Rain({ heavy }: { heavy?: boolean }) {
  const count = heavy ? 60 : 34;
  const drops = Array.from({ length: count }, (_, i) => ({
    left: (i * 173) % 100,
    delay: ((i * 91) % 100) / 100,
    dur: 0.45 + (((i * 53) % 40) / 100),
  }));
  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
      {drops.map((d, i) => (
        <span
          key={i}
          className="rain-fall absolute top-0 h-4 w-px bg-blue-200/70"
          style={
            {
              left: `${d.left}%`,
              animationDelay: `${d.delay}s`,
              "--rain-dur": `${d.dur}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

function Snow() {
  const flakes = Array.from({ length: 40 }, (_, i) => ({
    left: (i * 149) % 100,
    delay: ((i * 71) % 100) / 100,
    dur: 3 + (((i * 37) % 40) / 10),
    size: 2 + (i % 3),
  }));
  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
      {flakes.map((f, i) => (
        <span
          key={i}
          className="snow-fall absolute top-0 rounded-full bg-white"
          style={
            {
              left: `${f.left}%`,
              width: `${f.size}px`,
              height: `${f.size}px`,
              animationDelay: `${f.delay}s`,
              "--snow-dur": `${f.dur}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

/** Ox-drawn covered wagon with spinning wheels and a ⚡ mark on the canvas. */
function Wagon({ moving }: { moving: boolean }) {
  return (
    <div className={cn(moving && "wagon-bob")}>
      <svg width="150" height="86" viewBox="0 0 150 86" className="drop-shadow-sm">
        {/* ---- Ox team (left) ---- */}
        <g transform="translate(2,34)">
          <ellipse cx="24" cy="18" rx="22" ry="12" className="fill-amber-800 dark:fill-amber-700" />
          <path d="M2 14 q-8 0 -9 8 q0 6 7 6 l6 -2 z" className="fill-amber-900 dark:fill-amber-800" />
          <path d="M-4 12 q-4 -5 -1 -8" className="fill-none stroke-stone-200" strokeWidth="2" />
          <g className={cn(moving && "leg-a")}>
            <rect x="10" y="26" width="4" height="16" rx="2" className="fill-amber-900 dark:fill-amber-800" />
          </g>
          <g className={cn(moving && "leg-b")}>
            <rect x="34" y="26" width="4" height="16" rx="2" className="fill-amber-900 dark:fill-amber-800" />
          </g>
          <line x1="44" y1="18" x2="60" y2="22" className="stroke-amber-950 dark:stroke-amber-900" strokeWidth="3" />
        </g>

        {/* ---- Wagon body ---- */}
        <g transform="translate(58,6)">
          <rect x="4" y="42" width="80" height="18" rx="3" className="fill-amber-950 dark:fill-amber-900" />
          <path
            d="M6 44 Q 8 8 44 8 Q 80 8 82 44 Z"
            className="fill-stone-100 dark:fill-stone-300"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <g className="stroke-stone-400/70" strokeWidth="1.5" fill="none">
            <path d="M24 12 Q 22 30 20 44" />
            <path d="M44 9 L44 44" />
            <path d="M64 12 Q 66 30 68 44" />
          </g>
          <text
            x="44"
            y="34"
            textAnchor="middle"
            fontSize="26"
            fontWeight="bold"
            fontFamily="Georgia, 'Times New Roman', serif"
            className="fill-green-600 dark:fill-green-400"
          >
            B
          </text>
          <g transform="translate(22,60)">
            <Wheel spinning={moving} />
          </g>
          <g transform="translate(66,60)">
            <Wheel spinning={moving} />
          </g>
        </g>
      </svg>
    </div>
  );
}

function Wheel({ spinning }: { spinning: boolean }) {
  return (
    <g>
      <circle r="11" className="fill-none stroke-stone-800 dark:stroke-stone-200" strokeWidth="3" />
      <g className={cn(spinning && "wheel-spin")}>
        <circle r="3" className="fill-stone-800 dark:fill-stone-200" />
        <g className="stroke-stone-800 dark:stroke-stone-200" strokeWidth="2">
          <line x1="0" y1="-10" x2="0" y2="10" />
          <line x1="-10" y1="0" x2="10" y2="0" />
          <line x1="-7" y1="-7" x2="7" y2="7" />
          <line x1="-7" y1="7" x2="7" y2="-7" />
        </g>
      </g>
    </g>
  );
}
